import * as React from 'react';
import clsx from 'clsx';

import { Icon } from '../icon/icon.tsx';
import { Hint, type HintContent } from '../hint/hint.tsx';

import './text-area.css';

interface TextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  /** Instruction shown below the field at all times, alongside any state message. */
  helperText?: string;
  /** Content behind the "?" beside the label; without it there is no "?". */
  hint?: HintContent;
  required?: boolean;
  state?: 'default' | 'error' | 'warning' | 'validating' | 'correct';
  stateText?: string;
  stateDefaultIcon?: React.ReactNode;
  readOnly?: boolean;
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      disabled,
      onChange,
      // Soft limit: drives the counter only. Forwarding it to the DOM would
      // silently truncate a paste, which is the divergence it exists to surface.
      maxLength,
      helperText,
      'aria-describedby': ariaDescribedBy,
      hint,
      state = 'default',
      required,
      stateText,
      stateDefaultIcon,
      label,
      ...props
    },
    forwardedRef,
  ) => {
    const [clicked, setClicked] = React.useState(false);
    const charCount = String(props.value ?? '').length;
    const [heightValue, setHeightValue] = React.useState('');
    const iconColorRegistry = {
      default: 'var(--color_primary-dark)',
      validating: 'var(--color_system)',
      error: 'var(--color_error-dark)',
      warning: 'var(--color_warning)',
      correct: 'var(--color_success)',
      disabled: 'var(--color_disabled)',
    };
    const cleanState =
      state === 'validating' || state === 'correct' ? 'default' : state;
    const helperTextId = helperText
      ? `${props.id || props.name}-helper-text`
      : undefined;
    const describedBy =
      [ariaDescribedBy, helperTextId].filter(Boolean).join(' ') || undefined;

    const ref = React.useRef<HTMLTextAreaElement>(null);
    React.useImperativeHandle(
      forwardedRef,
      () => ref.current as HTMLTextAreaElement,
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    React.useEffect(() => {
      if (ref.current) {
        if (ref.current.value === '') {
          ref.current.style.height = '44px';
        }
        if (ref.current.scrollHeight > 44) {
          // Pad before measuring, or the padding clips the last line.
          ref.current.style.paddingBottom = '13px';
          ref.current.style.height = `${ref.current.scrollHeight}px`;
        }
      }
    }, [heightValue]);

    React.useEffect(() => {
      setClicked(props.value !== undefined && props.value !== '');
    }, [props.value]);

    return (
      <div
        className={clsx('text-area-wrapper', `textarea-${cleanState}`, {
          'textarea-disabled': disabled,
          'textarea-with-hint': hint,
        })}
      >
        <div className="main-wrapper">
          <label
            className={`label-${cleanState} ${
              clicked && disabled !== true ? `clicked-${cleanState}` : ''
            }`}
            htmlFor={props.id || props.name}
          >
            {label}{' '}
            {required && (
              <span style={{ color: 'var(--color_error)' }}>*</span>
            )}
          </label>
          <textarea
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              onChange?.(e);
              setHeightValue(e.target.value);
              e.target.value !== '' ? setClicked(true) : setClicked(false);
            }}
            ref={ref}
            {...props}
            aria-describedby={describedBy}
          />
        </div>
        {/* Follows the field so it paints over the border; a z-index would also lift it over the sticky mobile header. */}
        {hint && (
          <div className="top-wrapper">
            <Hint title={label} {...hint} />
          </div>
        )}
        {helperText && (
          <div id={helperTextId} className="helper-text">
            {helperText}
          </div>
        )}
        {stateText && (
          <div className="bottom-wrapper">
            <div className="left">
              {stateDefaultIcon ? (
                stateDefaultIcon
              ) : (
                <Icon
                  type={
                    state === 'error'
                      ? 'x'
                      : state === 'warning'
                      ? 'warning'
                      : state === 'validating'
                      ? 'loading'
                      : state === 'correct'
                      ? 'success'
                      : null
                  }
                  color={
                    disabled === true
                      ? iconColorRegistry.disabled
                      : iconColorRegistry[state]
                  }
                />
              )}
              {state !== 'validating' && state !== 'correct' ? (
                <div className="state-text">{stateText}</div>
              ) : state === 'correct' ? (
                <div className="state-text_correct">Correct!</div>
              ) : (
                <div className="state-text_validating"> Validating...</div>
              )}
            </div>
            <div className="right">
              {maxLength && (
                <span
                  className={
                    charCount > maxLength
                      ? 'char-count char-count--over'
                      : 'char-count'
                  }
                >{`${charCount}/${maxLength}`}</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

export { TextArea };
